import React from 'react';
import { siteData } from '../data';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = React.useState(false);

  return (
    <nav className="fixed w-full z-50 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between py-4 lg:py-0 lg:h-20">
          <div className="flex items-center justify-between w-full lg:w-auto mb-4 lg:mb-0">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="text-2xl font-bold tracking-tight text-gray-900">
                {siteData.name.split('service')[0]}
                <span className="text-blue-600">service</span>
              </Link>
            </div>
            
            <div className="md:hidden">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-gray-600">
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
          
          <div className="hidden md:flex md:flex-row md:items-center space-x-6 lg:space-x-8">
            <Link to="/" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Forside</Link>
            
            <DropdownMenu.Root open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
              <DropdownMenu.Trigger asChild>
                <button className="flex items-center space-x-1 text-gray-600 hover:text-blue-600 font-medium transition-colors cursor-pointer outline-none group">
                  <span>Services</span>
                  <ChevronDown size={16} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
              </DropdownMenu.Trigger>

              <AnimatePresence>
                {isDropdownOpen && (
                  <DropdownMenu.Portal forceMount>
                    <DropdownMenu.Content 
                      forceMount
                      asChild
                      className="min-w-[220px] z-50 mt-2"
                      sideOffset={5}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                        className="bg-white border border-gray-100 p-2 shadow-xl origin-top"
                      >
                        {siteData.services.map((service, index) => (
                          <DropdownMenu.Item 
                            key={index}
                            className="outline-none"
                            asChild
                            onSelect={() => setIsDropdownOpen(false)}
                          >
                            <Link 
                              to={`/services/${service.slug}`} 
                              className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                            >
                              {service.title}
                            </Link>
                          </DropdownMenu.Item>
                        ))}
                      </motion.div>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                )}
              </AnimatePresence>
            </DropdownMenu.Root>

            <Link to="/om-os" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Om os</Link>
            <Link to="/kontakt" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Kontakt</Link>
            <Link 
              to="/kontakt"
              className="bg-blue-600 text-white px-6 py-2.5 md:px-4 md:py-2 md:text-sm lg:px-4 lg:py-2 xl:px-6 xl:py-2.5 rounded-none font-semibold hover:bg-blue-700 transition-all shadow-sm w-full md:w-auto text-center"
            >
              Bestil vinduespudser her
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[60] bg-white flex flex-col p-6 overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-8">
              <Link to="/" className="text-2xl font-bold tracking-tight text-gray-900" onClick={() => setIsMobileMenuOpen(false)}>
                {siteData.name.split('service')[0]}
                <span className="text-blue-600">service</span>
              </Link>
              <button onClick={() => setIsMobileMenuOpen(false)} className="text-gray-600 p-2">
                <X size={32} />
              </button>
            </div>

            <div className="flex flex-col space-y-6">
              <Link 
                to="/" 
                className="text-2xl font-semibold text-gray-900 border-b border-gray-100 pb-4" 
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Forside
              </Link>
              
              <div className="border-b border-gray-100 pb-4">
                <button 
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className="flex items-center justify-between w-full text-2xl font-semibold text-gray-900"
                >
                  <span>Services</span>
                  <ChevronDown size={28} className={`transition-transform duration-200 text-gray-400 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {isMobileServicesOpen && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 space-y-4 overflow-hidden"
                    >
                      {siteData.services.map((service, index) => (
                        <Link 
                          key={index}
                          to={`/services/${service.slug}`} 
                          className="block text-xl font-medium text-blue-600 pl-4 border-l-4 border-blue-600" 
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            setIsMobileServicesOpen(false);
                          }}
                        >
                          {service.title}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link 
                to="/om-os" 
                className="text-2xl font-semibold text-gray-900 border-b border-gray-100 pb-4" 
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Om os
              </Link>
              
              <Link 
                to="/kontakt" 
                className="text-2xl font-semibold text-gray-900 border-b border-gray-100 pb-4" 
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Kontakt
              </Link>
              
              <div className="pt-4">
                <Link 
                  to="/kontakt"
                  className="block w-full text-center bg-blue-600 text-white px-6 py-5 rounded-none font-bold text-xl shadow-lg hover:bg-blue-700 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Bestil vinduespudser her
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </nav>
  );
}
